<!--
  ONE template for all three roles. `network`, `serviceProvider` and
  `organization` render this same markup against the same payload; role
  changes only which ACTIONS appear, never what is shown. See the component
  class comment for why.
-->
<!--
  The standard app shell. The SideNav focus differs by role because the two
  routes into this component are different menu entries: an operator arrived
  from "Project Mapping", a customer from "Review Project Mapping", and
  highlighting the other one tells them they are somewhere they are not.
-->
<div class="app-container" [class.is-mobile]="mobileQuery.matches">
  <mat-toolbar color="primary" class="app-toolbar">
    <div class="app-toolbar-sections">
      <!-- Start: Hamburger + Title -->
      <div class="app-toolbar-start">
        <button mat-icon-button (click)="mainSideNav.toggle()"><mat-icon>menu</mat-icon></button>
        <span class="app-toolbar-title" i18n="@@Review Project Mapping">Review Project Mapping</span>
      </div>

      <!-- End -->
      <div class="app-toolbar-end">
        <app-shopping-cart-top-bar-button></app-shopping-cart-top-bar-button>
        <app-active-role></app-active-role>
      </div>
    </div>
  </mat-toolbar>
  <mat-sidenav-container class="app-sidenav-container" (backdropClick)="mainSideNav.close()" [style.marginTop.px]="mobileQuery.matches ? 56 : 0">
    <mat-sidenav opened #mainSideNav [mode]="mobileQuery.matches ? 'over' : 'side'" (keydown.escape)="mainSideNav.close()" [fixedInViewport]="mobileQuery.matches" fixedTopGap="56">
      <mat-nav-list>
        <app-side-nav [focus]="isOperator ? 'projectmapping' : 'projectreview'"></app-side-nav>
      </mat-nav-list>
    </mat-sidenav>
    <mat-sidenav-content class="m2app-dark">
      <div class="app-content">

<mat-card class="mappingReview">

  <div class="reviewHeader">
    <div class="reviewHeaderMain">
      <button mat-icon-button *ngIf="isOperator" (click)="backToDashboard()"
              title="Back to the mapping dashboard" i18n-title="@@BackToMappingDashboardToolTip">
        <mat-icon>arrow_back</mat-icon>
      </button>
      <h2 class="reviewTitle" i18n="@@Review Project Mapping">Review Project Mapping</h2>
      <!--
        Operators only, and only because they arrive here from a table of
        several hundred organizations. Without it every one of those rows
        opens a page with an identical heading and nothing on it that says
        which organization was actually opened.
      -->
      <!--
        The NAME matters as much as the id, and for a reason that cost a round
        trip on 2026-10-28: a proposal is named after its primary Book, so a
        Project name and an account name sit on this screen looking alike. An
        operator reported a fault against "the Winder Log organization"; Winder
        Log is a book title and the account was Mitsubishi Polyester Films.

        Name is the customer's own data and is shown verbatim - no i18n. The
        id stays, and stays last: it is what gets pasted into a support ticket,
        and it is the only one of the two that is unambiguous.
      -->
      <span class="reviewOrgId" *ngIf="isOperator && orgId">
        <span i18n="@@Organization">Organization</span>
        <span class="orgNameValue" *ngIf="orgName">{{ orgName }}</span>
        <span class="orgIdValue">{{ orgId | numberFormat }}</span>
      </span>
    </div>
    <div class="reviewHeaderActions">
      <button mat-stroked-button *ngIf="canPlan" (click)="plan()" [disabled]="loading"
              title="Work out proposed groupings again. This reads only - nothing is created."
              i18n-title="@@ReplanMappingToolTip">
        <mat-icon>refresh</mat-icon>
        <span i18n="@@Re-plan">Re-plan</span>
      </button>
      <button mat-stroked-button *ngIf="canRevert" (click)="revert()" [disabled]="applying || writeUnavailable"
              title="Undo everything this run created" i18n-title="@@RevertMappingRunToolTip">
        <mat-icon>undo</mat-icon>
        <span i18n="@@Revert Run">Revert Run</span>
      </button>
    </div>
  </div>

  <!--
    An explanation of what this screen is, shown to everyone. A customer
    arrives here without having asked for it, frequently after a phone call,
    and "why am I being asked this?" has to be answerable from the screen.

    Hidden when the load failed. It promises "Below is what we propose" and
    "Each item says why it was put where it was" -- about a list that is not
    there, printed directly above the sentence admitting it could not be
    fetched. Read together those suggest a half-rendered page rather than a
    failed request, which sends the reader looking for a list instead of
    retrying.
  -->
  <p class="reviewIntro" *ngIf="!loadFailed && !notReady" i18n="@@MappingReviewIntro">
    Your books and drafts are being grouped into Projects, so that every version of the same work sits together.
    Below is what we propose. Each item says why it was put where it was. Nothing is changed until you apply it.
  </p>

  <!--
    How the cards are arranged, said once and up front.

    The layout encodes a rule nobody can infer by looking: items are ordered
    newest-first within their kind, and each card's stated reason describes the
    card immediately BEFORE it of the same kind - with the first Draft compared
    against the first Book. Without that sentence the green/orange/pink
    comparison colouring looks arbitrary, because the reader does not know what
    each card is being compared against.

    Server-side ordering rule: ProjectMappingReview.md §3.3, planner 1.2.0.
  -->
  <p class="readingOrder" *ngIf="!loadFailed && !notReady" i18n="@@MappingReadingOrder">
    In each group, Books come first with the newest on the left, then Drafts with the newest first.
    Each item is compared with the one before it of the same kind, and the first Draft is compared with the first Book.
  </p>

  <div class="loadingRow" *ngIf="loading">
    <mat-spinner diameter="24"></mat-spinner>
    <span i18n="@@Loading">Loading</span>
  </div>

  <!--
    Stated once, at the top, instead of a red toast per button press.

    Every write action on this screen posts to §3.4-§3.8, and the server has
    not registered any of them yet (see mapping-write-unavailable.ts). Until
    it does, the honest thing is to say so where the reader is already looking
    and to stop offering the buttons -- an operator who is told "could not be
    applied" retries, and retrying is the one thing that cannot help.

    Phrased for both audiences, because this screen has two: the customer is
    told their answers cannot be recorded, which is what matters to them, and
    the operator is additionally told nothing was created.
  -->
  <!--
    An apply is ENQUEUED, not done, and until 2026-10-28 this screen said
    nothing at all between the press and the finish - the buttons simply went
    dead. Shown for revert too, which rides the same queue.

    The timed-out branch matters as much as the waiting one: a session that
    missed the PROJECT_MAPPING_RUN burst (queues are collected after 15
    minutes) would otherwise sit on a spinner forever, and "still working" is
    a statement the screen can see is false once it has stopped checking.
  -->
  <div class="runWaitPanel" *ngIf="applying || runWaitTimedOut">
    <mat-spinner diameter="20" *ngIf="!runWaitTimedOut"></mat-spinner>
    <mat-icon *ngIf="runWaitTimedOut">hourglass_disabled</mat-icon>
    <div class="runWaitText">
      <span class="runWaitHeadline" *ngIf="isReverting" i18n="@@MappingRevertingHeadline">This run is being undone.</span>
      <span class="runWaitHeadline" *ngIf="!isReverting" i18n="@@MappingApplyingHeadline">Projects are being created.</span>
      <span class="runWaitHint" *ngIf="!runWaitTimedOut" i18n="@@MappingApplyingHint">This page updates itself when it finishes. There is nothing else to do.</span>
      <span class="runWaitHint" *ngIf="runWaitTimedOut" i18n="@@MappingRunTakingLongHint">This is taking longer than usual. The page has stopped checking on its own, and the work is still going on in the background.</span>
    </div>
    <button mat-stroked-button *ngIf="runWaitTimedOut" (click)="retryRunWait()"
            title="Reload" i18n-title="@@RefreshToolTip">
      <mat-icon>refresh</mat-icon>
      <span i18n="@@Check again">Check again</span>
    </button>
  </div>

  <!--
    Which failure it was, for whoever is asked about it later.

    Same shape and the same label as the errorDetail block on the not-ready
    panel, deliberately: a reader who has seen one should recognise the other
    as "this is the part I quote to support", not read it as a second error.
  -->
  <div class="errorDetail writeErrorDetail" *ngIf="writeErrorDetail">
    <span class="errorDetailLabel" i18n="@@Technical detail">Technical detail</span>
    <span class="errorDetailText">{{ writeErrorDetail }}</span>
  </div>

  <div class="writeUnavailablePanel" *ngIf="writeUnavailable">
    <mat-icon>cloud_off</mat-icon>
    <div class="writeUnavailableText">
      <span class="writeUnavailableHeadline" i18n="@@MappingWritesUnavailableHeadline">This part of the service is not available yet.</span>
      <span class="writeUnavailableHint" *ngIf="isOperator" i18n="@@MappingWritesUnavailableOperatorHint">Answers cannot be recorded and Projects cannot be created on this server yet. Nothing has been created and nothing has been changed. The groupings below are correct and can still be read.</span>
      <span class="writeUnavailableHint" *ngIf="!isOperator" i18n="@@MappingWritesUnavailableCustomerHint">Your answers cannot be saved yet. Nothing has been changed. You can still read the proposed groupings below.</span>
    </div>
  </div>

  <!--
    NOT an error, and this is the panel that makes the screen usable at all.

    The review endpoint answers with a SmartError in five situations that are
    perfectly normal, and the commonest of them -- 404
    PROJECT_MAPPING_NOT_PLANNED -- is the state EVERY organization is in until
    somebody plans it. Rendering that as the red "could not be loaded" panel
    meant every row on the dashboard led to a screen reporting an outage, with
    no hint that the thing to do was press Plan. See mapping-not-ready.ts.

    Each branch below says what is true and offers the one action that moves
    it forward. The technical detail stays, labelled and secondary, because
    the server's sentence is English in all 21 bundles and can never take the
    position of the message meant for the reader.
  -->
  <div class="notReadyPanel" *ngIf="notReady && !loading" [ngClass]="'notReady-' + notReady">
    <ng-container [ngSwitch]="notReady">

      <div class="notReadyMain" *ngSwitchCase="'NOT_PLANNED'">
        <mat-icon>hourglass_empty</mat-icon>
        <div class="notReadyText">
          <span class="notReadyHeadline" *ngIf="isOperator" i18n="@@MappingNotPlannedHeadline">This organization has not been planned yet.</span>
          <span class="notReadyHeadline" *ngIf="!isOperator" i18n="@@MappingNotPlannedCustomerHeadline">Your books and drafts have not been grouped yet.</span>
          <span class="notReadyHint" *ngIf="isOperator" i18n="@@MappingNotPlannedOperatorHint">Planning works out which books and drafts belong together. It only reads: no Project is created and the customer sees nothing until you apply it.</span>
          <span class="notReadyHint" *ngIf="!isOperator" i18n="@@MappingNotPlannedCustomerHint">There is nothing for you to do. We will ask you if we need you to decide anything.</span>
        </div>
      </div>

      <div class="notReadyMain" *ngSwitchCase="'PLANNING'">
        <mat-spinner diameter="20"></mat-spinner>
        <div class="notReadyText">
          <span class="notReadyHeadline" i18n="@@MappingPlanInProgressHeadline">A plan is being built for this organization.</span>
          <span class="notReadyHint" *ngIf="!planWaitTimedOut" i18n="@@MappingPlanInProgressHint">This is usually ready within seconds. The page updates itself when it is.</span>
          <!--
            Said out loud when we stop waiting. Leaving "usually ready within
            seconds" on screen after two minutes of not being ready is a
            statement the screen can see is false.
          -->
          <span class="notReadyHint" *ngIf="planWaitTimedOut" i18n="@@MappingPlanTakingLongHint">This is taking longer than usual. The page has stopped checking on its own.</span>
        </div>
      </div>

      <div class="notReadyMain" *ngSwitchCase="'FAILED'">
        <mat-icon color="warn">error_outline</mat-icon>
        <div class="notReadyText">
          <span class="notReadyHeadline" i18n="@@MappingPlanFailedHeadline">The last planning run did not finish.</span>
          <span class="notReadyHint" i18n="@@MappingPlanFailedHint">Nothing was created, so planning again is safe.</span>
        </div>
      </div>

      <div class="notReadyMain" *ngSwitchCase="'EXPIRED'">
        <mat-icon>history</mat-icon>
        <div class="notReadyText">
          <span class="notReadyHeadline" i18n="@@MappingPlanExpiredHeadline">The stored plan is no longer available.</span>
          <span class="notReadyHint" i18n="@@MappingPlanExpiredHint">Planning again costs only the time to work it out. Any answers already given are kept.</span>
        </div>
      </div>

      <div class="notReadyMain" *ngSwitchCase="'FORBIDDEN'">
        <mat-icon color="warn">lock</mat-icon>
        <div class="notReadyText">
          <span class="notReadyHeadline" i18n="@@MappingReviewForbiddenHeadline">This organization's mapping is not yours to review.</span>
        </div>
      </div>

    </ng-container>

    <div class="notReadyActions">
      <!-- Operator only: planning is not a concept a customer should hold. -->
      <button mat-flat-button color="primary" *ngIf="planWouldHelp"
              (click)="plan()" [disabled]="loading"
              title="Work out proposed groupings. This reads only - nothing is created."
              i18n-title="@@PlanMappingToolTip">
        <span i18n="@@Plan">Plan</span>
      </button>
      <button mat-stroked-button *ngIf="planWaitTimedOut" (click)="retryPlanWait()" [disabled]="loading"
              title="Reload" i18n-title="@@RefreshToolTip">
        <mat-icon>refresh</mat-icon>
        <span i18n="@@Refresh">Refresh</span>
      </button>
    </div>

    <div class="errorDetail" *ngIf="loadErrorDetail">
      <span class="errorDetailLabel" i18n="@@Technical detail">Technical detail</span>
      <span class="errorDetailText">{{ loadErrorDetail }}</span>
    </div>
  </div>

  <!--
    A failed load must never render as an empty list. "Nothing needs your
    attention" is the single most damaging wrong answer this screen can give,
    because it closes the question rather than raising it.

    Reached only for errors mapping-not-ready.ts does NOT recognise -- an
    unknown code is not something to reassure anybody about.
  -->
  <div class="errorPanel" *ngIf="loadFailed">
    <mat-icon color="warn">error_outline</mat-icon>
    <span i18n="@@MappingReviewLoadFailed">The proposed groupings could not be loaded, so this list is not complete. Please try again.</span>
    <!--
      Technical, labelled, secondary. Neither the server's prose (English in
      all 21 bundles) nor Angular's own diagnostic may occupy the position of
      the message meant for the reader.
    -->
    <div class="errorDetail" *ngIf="loadErrorDetail">
      <span class="errorDetailLabel" i18n="@@Technical detail">Technical detail</span>
      <span class="errorDetailText">{{ loadErrorDetail }}</span>
    </div>
  </div>

  <ng-container *ngIf="!loading && !loadFailed && review">

    <div class="statusStrip floatContainer">
      <span class="statusChip" [ngClass]="'state-' + review.state" [ngSwitch]="review.state">
        <span *ngSwitchCase="'NOT_PLANNED'"       i18n="@@MappingStateNotPlanned">Not yet planned</span>
        <span *ngSwitchCase="'PLAN_STALE'"        i18n="@@MappingStatePlanStale">New items since the last plan</span>
        <span *ngSwitchCase="'READY_SIMPLE'"      i18n="@@MappingStateReadySimple">Ready, nothing to decide</span>
        <span *ngSwitchCase="'READY_PARTIAL'"     i18n="@@MappingStateReadyPartial">Ready, some decisions needed</span>
        <span *ngSwitchCase="'AWAITING_CUSTOMER'" i18n="@@MappingStateAwaitingCustomer">Waiting on your answers</span>
        <span *ngSwitchCase="'CUSTOMER_DECIDED'"  i18n="@@MappingStateCustomerDecided">Answered, ready to apply</span>
        <span *ngSwitchCase="'COMPLETE'"          i18n="@@MappingStateComplete">Complete</span>
        <span *ngSwitchDefault>{{ review.state }}</span>
      </span>
      <span class="countChip">
        <span i18n="@@Needs a decision">Needs a decision</span>
        <span class="countValue">{{ review.counts.needsDecision | numberFormat }}</span>
      </span>
      <span class="countChip">
        <span i18n="@@Already grouped">Already grouped</span>
        <span class="countValue">{{ review.counts.applied | numberFormat }}</span>
      </span>
      <span class="countChip" *ngIf="review.counts.singleton">
        <span i18n="@@On their own">On their own</span>
        <span class="countValue">{{ review.counts.singleton | numberFormat }}</span>
      </span>
      <span class="phaseChip" *ngIf="review.phase === 'APPLYING' || applying">
        <mat-spinner diameter="16"></mat-spinner>
        <span i18n="@@MappingApplying">Building Projects&hellip;</span>
      </span>
    </div>

    <!-- Never planned. Not an error, and for a customer not even actionable. -->
    <div class="emptyPanel" *ngIf="!hasRun">
      <mat-icon>hourglass_empty</mat-icon>
      <span *ngIf="!isOperator" i18n="@@MappingNotPlannedCustomer">Nothing has been proposed for your account yet. There is nothing for you to do.</span>
      <span *ngIf="isOperator" i18n="@@MappingNotPlannedOperator">This organization has never been planned. Use Re-plan to work out proposed groupings.</span>
    </div>

    <!-- Planned, but there is genuinely nothing to ask. -->
    <div class="emptyPanel" *ngIf="hasRun && proposalViews.length === 0">
      <mat-icon>check_circle</mat-icon>
      <span i18n="@@MappingNothingToReview">Nothing needs your attention. Every book and draft is already filed where it belongs.</span>
    </div>

    <!--
      Why the cards are mostly empty.

      Shown instead of leaving a wall of em-dashes unexplained. An operator is
      reviewing another organization's items, and every id in this payload
      belongs to that organization - so the per-item lookups this screen would
      otherwise make are answered in the WRONG scope and return nothing. See
      `hydrationBlocked`.

      This disappears the moment the server sends `display` (contract §3.3),
      because then there is nothing to look up.
    -->
    <div class="hydrationPanel" *ngIf="hydrationBlocked && proposalViews.length > 0">
      <mat-icon>info_outline</mat-icon>
      <div class="hydrationText">
        <span class="hydrationHeadline" i18n="@@MappingValuesUnavailableHeadline">Item details are not available on this screen yet.</span>
        <span class="hydrationHint" i18n="@@MappingValuesUnavailableHint">Titles, PDF references and dates belong to the organization being reviewed, so they cannot be looked up from here. They will appear once they are included in the mapping itself.</span>
      </div>
    </div>

    <!--
      The key to the colours.

      Collapsed by default and placed once, above the list rather than on each
      card: it is a reference, not something to re-read per group. It is also
      the only place the ordinal scale is spelled out, which matters because
      the contract forbids a confidence number ever reaching this screen - the
      words below are the entire grading the reader is given.
    -->
    <div class="facetLegend" *ngIf="proposalViews.length > 0">
      <button class="legendToggle" type="button" (click)="toggleLegend()"
              [attr.aria-expanded]="legendOpen">
        <mat-icon>{{ legendOpen ? 'expand_more' : 'chevron_right' }}</mat-icon>
        <span i18n="@@MappingFacetLegendHeading">What the colours mean</span>
      </button>
      <div class="legendBody" *ngIf="legendOpen">
        <p class="legendIntro" i18n="@@MappingFacetLegendIntro">
          Every book and draft below shows the same details in the same order, so you can compare them.
          A detail is coloured when it is part of the reason the items were grouped.
        </p>
        <div class="legendItems">
          <span class="legendItem facet-ANCHOR">
            <span class="legendSwatch"></span>
            <span i18n="@@FacetStrengthAnchorLong">Starting point &mdash; the item the others were matched against</span>
          </span>
          <span class="legendItem facet-DECISIVE">
            <span class="legendSwatch"></span>
            <span i18n="@@FacetStrengthDecisiveLong">Match &mdash; this is why the item is in this group</span>
          </span>
          <span class="legendItem facet-SUPPORTING">
            <span class="legendSwatch"></span>
            <span i18n="@@FacetStrengthSupportingLong">Also matches &mdash; backs up the main reason</span>
          </span>
          <span class="legendItem facet-CORROBORATING">
            <span class="legendSwatch"></span>
            <span i18n="@@FacetStrengthCorroboratingLong">Agrees only &mdash; never enough to group items on its own</span>
          </span>
          <span class="legendItem facet-WEAK">
            <span class="legendSwatch"></span>
            <span i18n="@@FacetStrengthWeakLong">Close &mdash; similar, but not identical</span>
          </span>
          <span class="legendItem facet-DIFFERS">
            <span class="legendSwatch"></span>
            <span i18n="@@FacetStrengthDiffersLong">Differs &mdash; this detail does not match the others</span>
          </span>
          <span class="legendItem facet-NEUTRAL">
            <span class="legendSwatch"></span>
            <span i18n="@@FacetStrengthNeutralLong">Grey &mdash; shown for comparison, not part of the match</span>
          </span>
        </div>
      </div>
    </div>

    <!--
      Why most of the list is not on screen.

      The largest organization in the estate plans to 1,768 proposals, nearly
      all of them single items nobody is being asked about. Rendering every one
      costs ~25-30k DOM nodes of Material card to show a reader a handful of
      questions buried in a catalogue.

      Stated plainly rather than silently trimmed: a list that quietly omits
      most of itself is worse than a long one, because the reader cannot tell
      whether an item is absent or merely not reached. The count of what is
      held back, and a way to see it, are both on screen.
    -->
    <div class="trimPanel" *ngIf="largeReview && hiddenProposalCount > 0">
      <mat-icon>filter_list</mat-icon>
      <div class="trimText">
        <span class="trimHeadline" *ngIf="outstandingCount > 0" i18n="@@MappingTrimmedHeadline">Showing the groups that need a decision.</span>
        <span class="trimHeadline" *ngIf="outstandingCount === 0" i18n="@@MappingTrimmedSettledHeadline">Nothing here needs a decision. Showing the first groups.</span>
        <span class="trimHint">
          <span i18n="@@MappingTrimmedHint">Others are already settled and are not shown, to keep this page quick</span>
          <span class="countValue">{{ hiddenProposalCount | numberFormat }}</span>
        </span>
      </div>
      <div class="trimActions">
        <!--
          Exactly the groups where something was considered and rejected.
          They sort directly below the questions, so "show more" would surface
          them anyway - but a reader after these specifically should not have to
          reveal an arbitrary batch and hope.
        -->
        <button mat-stroked-button class="alternativesButton" *ngIf="hiddenAlternativeCount > 0"
                (click)="revealAlternatives()">
          <mat-icon>compare_arrows</mat-icon>
          <span i18n="@@MappingShowAlternatives">With alternatives</span>
          <span class="countValue">{{ hiddenAlternativeCount | numberFormat }}</span>
        </button>
        <button mat-stroked-button (click)="revealMore()">
          <span i18n="@@MappingShowMore">Show more</span>
          <span class="countValue">{{ revealBatchSize | numberFormat }}</span>
        </button>
        <!--
          The count sits ON the button so the cost of pressing it is visible
          before it is paid.
        -->
        <button mat-button (click)="revealAll()">
          <span i18n="@@MappingShowAll">Show all</span>
          <span class="countValue">{{ proposalViews.length | numberFormat }}</span>
        </button>
      </div>
    </div>

    <div class="proposalList" *ngIf="proposalViews.length > 0">
      <!--
        `proposals` arrives decision-needed first, and that ordering is kept.
        Everything else defaults collapsed so the list reads as a short set of
        questions rather than an entire catalogue.
      -->
      <mat-card class="proposalCard floatContainer"
                *ngFor="let view of visibleProposals; trackBy: trackProposal"
                [ngClass]="[
                  'band-' + view.proposal.band,
                  view.hasAlternatives ? 'hasAlternatives' : '',
                  view.needsDecision && !view.answered ? 'needsDecision' : '',
                  view.answered ? 'answered' : '',
                  view.applied ? 'applied' : ''
                ]">

        <div class="proposalHeader" (click)="toggleExpanded(view)">
          <mat-icon class="disclosure">{{ view.expanded ? 'expand_more' : 'chevron_right' }}</mat-icon>
          <div class="proposalHeading">
            <span class="proposalName">{{ view.proposal.proposedName }}</span>
            <span class="proposalSummary">
              <span i18n="@@MappingItemsInGroup">items in this group</span>
              <span class="countValue">{{ view.includedMembers.length | numberFormat }}</span>
            </span>
          </div>
          <div class="proposalBadges">
            <!--
              No chip for a SINGLETON.

              Singletons are the overwhelming majority -- 148 of 191 proposals
              in the server's own worked example -- so a chip reading "On its
              own" would appear on nearly every row and say nothing the single
              member list does not already show. "Decorate the common case and
              the signal stops meaning anything" is the settled badge-fatigue
              rule behind singletons never being flagged (server, 2026-10-23);
              this is the same rule applied to the band itself.

              The aggregate count in the status strip above is unaffected: a
              summary is not a badge.
            -->
            <span class="bandChip" [ngClass]="'band-' + view.proposal.band" [ngSwitch]="view.proposal.band"
                  *ngIf="view.proposal.band !== 'SINGLETON'">
              <span *ngSwitchCase="'CONFIDENT'" i18n="@@MappingBandConfident">Clear match</span>
              <span *ngSwitchCase="'REVIEW'"    i18n="@@MappingBandReview">Worth a look</span>
              <span *ngSwitchDefault>{{ view.proposal.band }}</span>
            </span>
            <!--
              Something was considered for this group and left out.

              Worth a chip where SINGLETON is not, by the same badge-fatigue
              rule: this is the minority case and it marks the groups whose
              reasoning is most contestable - the reader can see there is a
              rejected alternative without expanding the card. The count is on
              it because "one near miss" and "nine" are different situations.
            -->
            <span class="alternativesChip" *ngIf="view.hasAlternatives">
              <mat-icon>compare_arrows</mat-icon>
              <span i18n="@@MappingAlternativesChip">Alternatives considered</span>
              <span class="countValue">{{ (view.nearMisses.length + view.nearMissesTruncated) | numberFormat }}</span>
            </span>
            <!-- Visible while scanning, so a held proposal is not lost in the list. -->
            <span class="heldChip" *ngIf="isHeld(view)" i18n="@@MappingHeldChip">Held back</span>
            <span class="decisionChip" *ngIf="view.needsDecision && !view.answered" i18n="@@Needs your answer">Needs your answer</span>
            <span class="answeredChip" *ngIf="view.answered && !view.applied" i18n="@@Answered">Answered</span>
            <span class="appliedChip" *ngIf="view.applied" i18n="@@Created">Created</span>
            <span class="supersededChip" *ngIf="view.superseded" i18n="@@MappingSuperseded">Replaced by a newer plan</span>
          </div>
        </div>

        <!-- WHY you are being asked, before WHAT you are being asked. -->
        <div class="decisionReasons" *ngIf="view.needsDecision && !view.answered">
          <ng-container *ngFor="let reason of view.proposal.decisionReasons">
            <span class="decisionReason" [ngSwitch]="reason">
              <span *ngSwitchCase="'LOW_CONFIDENCE'"    i18n="@@DecisionReasonLowConfidence">We are not sure these are the same work.</span>
              <span *ngSwitchCase="'PRIMARY_AMBIGUOUS'" i18n="@@DecisionReasonPrimaryAmbiguous">More than one of these could be the current version.</span>
              <span *ngSwitchCase="'PRIMARY_STALE'"     i18n="@@DecisionReasonPrimaryStale">The version we would pick has not been touched in a long time.</span>
              <span *ngSwitchDefault>{{ reason }}</span>
            </span>
          </ng-container>
        </div>

        <div class="proposalBody" *ngIf="view.expanded">

          <!--
            Why this group was NOT asked about.

            Shown in the body rather than as a header chip: 5,861 proposals in
            the estate are cleared, so a chip on each would be noise on the
            overwhelming majority of cards - the same badge-fatigue rule that
            keeps SINGLETON off the chip row. A reader only wants this while
            spot-checking, and spot-checking means expanding.
          -->
          <div class="clearedPanel" *ngIf="view.cleared && !view.needsDecision">
            <mat-icon>verified</mat-icon>
            <div class="clearedText">
              <span class="clearedHeading" i18n="@@MappingClearedHeading">No answer needed</span>
              <span class="clearedReason" *ngFor="let code of view.clearedBy" [ngSwitch]="code">
                <span *ngSwitchCase="'SHARED_BOTH_RESOURCES'" i18n="@@ClearedBySharedBothResources">Both PDFs are the same file, so these print the same book.</span>
                <span *ngSwitchCase="'SAME_TITLE_SHARED_RESOURCE'" i18n="@@ClearedBySameTitleSharedResource">The titles are the same and a PDF is shared.</span>
                <span *ngSwitchCase="'SAME_TITLE'" i18n="@@ClearedBySameTitle">The titles are identical.</span>
                <span *ngSwitchCase="'IDENTIFIER_MATCH'" i18n="@@ClearedByIdentifierMatch">The published identifiers match.</span>
                <span *ngSwitchCase="'EXISTING_PROJECT'" i18n="@@ClearedByExistingProject">Someone already filed these together.</span>
                <span *ngSwitchDefault>{{ code }}</span>
              </span>
            </div>
          </div>

          <!--
            True, but NOT a question.

            `decisionReasons` can be non-empty on a cleared proposal: the
            grouping is proved, but the dates may still be ambiguous. Rendered
            as a note and deliberately NOT in the `decisionReasons` block
            above, which is styled and worded as an ask.
          -->
          <div class="primaryNote" *ngIf="!view.needsDecision && view.primaryNotes.length > 0">
            <span class="primaryNoteLabel" i18n="@@MappingPrimaryNoteLabel">Worth knowing</span>
            <span class="primaryNoteText" *ngFor="let reason of view.primaryNotes" [ngSwitch]="reason">
              <span *ngSwitchCase="'PRIMARY_AMBIGUOUS'" i18n="@@DecisionReasonPrimaryAmbiguous">More than one of these could be the current version.</span>
              <span *ngSwitchCase="'PRIMARY_STALE'" i18n="@@DecisionReasonPrimaryStale">The version we would pick has not been touched in a long time.</span>
            </span>
          </div>

          <!--
            A WRAPPING ROW of cards, not a vertical list.

            The question on this screen is "do these belong together?", which
            is a comparison between items, and a vertical list answers it
            badly: two items being compared can sit a screen apart, and the
            facet grids never line up side by side where the eye can run
            across them. Most groups are small enough to fit one screen width
            - the seed plus a handful of versions - so the common case becomes
            a single readable row, and the rare large group wraps rather than
            paging.
          -->
          <div class="memberFlow">
            <div class="memberCard"
                 *ngFor="let memberView of view.members"
                 [ngClass]="{ 'excluded': memberView.excluded,
                              'seed': memberView.member.joinReason === 'SEED',
                              'chosen': isPrimary(view, memberView) }">

              <div class="cardHead">
                <mat-icon class="memberIcon">{{ memberView.isBook ? 'menu_book' : 'edit_note' }}</mat-icon>
                <span class="memberType" *ngIf="memberView.isBook" i18n="@@Book">Book</span>
                <span class="memberType" *ngIf="!memberView.isBook" i18n="@@Draft">Draft</span>
                <span class="memberId">{{ memberView.member.item_ID | numberFormat }}</span>
                <!--
                  The seed is the item the cluster formed around. In a
                  vertical list that was drawn with a connector line; in a
                  wrapping row there is no reliable place to put a line, so
                  the relationship is said in words instead.
                -->
                <span class="seedBadge" *ngIf="memberView.member.joinReason === 'SEED'"
                      i18n="@@MappingSeedBadge">Starting point</span>
              </div>

              <div class="cardTitle" *ngIf="!memberView.unresolved" [title]="memberView.displayTitle">{{ memberView.displayTitle }}</div>
              <!--
                Still resolving. Shown as a state, not as a blank: an unnamed
                card would be read as an item with no title rather than as one
                whose title has not arrived.
              -->
              <div class="cardTitle unresolvedTitle" *ngIf="memberView.unresolved" i18n="@@MappingItemLoading">Loading this item&hellip;</div>

              <!--
                The same eight rows, in the same order, on every card. This is
                what the reader actually compares.
              -->
              <app-mapping-facet-grid [rows]="memberView.facetRows"
                                      [sequenceAnomaly]="memberView.sequenceAnomaly"></app-mapping-facet-grid>

              <div class="cardReason">
                <app-project-mapping-reason
                  [reason]="memberView.member.joinReason"
                  [joinedTo]="memberView.member.joinedTo"
                  [joinedToLabel]="memberView.joinedToLabel">
                </app-project-mapping-reason>
              </div>

              <div class="cardControls">
                <!--
                  The primary is labelled with the RULE that chose it, never
                  as "selected automatically", so the reader can disagree with
                  a reason rather than with an algorithm.
                -->
                <button mat-stroked-button class="primaryButton"
                        [ngClass]="{ 'isPrimary': isPrimary(view, memberView) }"
                        [disabled]="memberView.excluded || view.applied"
                        (click)="setPrimary(view, memberView)">
                  <mat-icon>{{ isPrimary(view, memberView) ? 'star' : 'star_border' }}</mat-icon>
                  <span i18n="@@Current version">Current version</span>
                </button>
                <!--
                  Exclude only. Adding an item to a group is a merge, which the
                  ordinary Projects UI already does well once the Project
                  exists - and a picker over an entire catalogue is a much
                  bigger question than "does this one belong?".
                -->
                <button mat-icon-button class="excludeButton"
                        [disabled]="view.applied"
                        (click)="toggleExcluded(view, memberView)"
                        title="Leave this item out of this group, or put it back"
                        i18n-title="@@ExcludeMemberToolTip">
                  <mat-icon>{{ memberView.excluded ? 'add_circle_outline' : 'remove_circle_outline' }}</mat-icon>
                </button>
              </div>
              <span class="primaryRule" *ngIf="memberView.member.isProposedPrimary && memberView.member.primaryRule"
                    [ngSwitch]="memberView.member.primaryRule">
                <span *ngSwitchCase="'RECENCY'" i18n="@@PrimaryRuleRecency">most recently edited</span>
                <span *ngSwitchCase="'ID'"      i18n="@@PrimaryRuleId">newest version</span>
                <span *ngSwitchDefault>{{ memberView.member.primaryRule }}</span>
              </span>
            </div>
          </div>

          <!--
            Near misses: things deliberately LEFT OUT. Showing them is what
            stops the screen reading as "the computer found these three" and
            makes it read as "these three, and here is what it considered and
            rejected".
          -->
          <div class="nearMisses" *ngIf="view.nearMisses.length > 0">
            <div class="nearMissHeading" i18n="@@MappingNearMissHeading">Considered but not included</div>
            <!--
              Without this the colours on these cards are ambiguous: the member
              cards above are each compared with their neighbour, these are
              each compared with the version being kept, and nothing on screen
              would distinguish the two.
            -->
            <div class="nearMissSubHeading" i18n="@@MappingNearMissComparedWith">Each one below is compared with the version this group is keeping.</div>
            <!--
              Drawn as cards in the same wrapping flow as the members above,
              with the same facet grid. That is what lets the reader see the
              near miss matched on a cover and the members matched on an
              interior PDF, rather than taking it on trust.
            -->
            <div class="memberFlow">
              <div class="memberCard nearMissCard" *ngFor="let nearMiss of view.nearMisses">
                <div class="cardHead">
                  <mat-icon class="memberIcon">{{ nearMiss.isBook ? 'menu_book' : 'edit_note' }}</mat-icon>
                  <span class="memberType" *ngIf="nearMiss.isBook" i18n="@@Book">Book</span>
                  <span class="memberType" *ngIf="!nearMiss.isBook" i18n="@@Draft">Draft</span>
                  <span class="memberId">{{ nearMiss.ref.item_ID | numberFormat }}</span>
                </div>

                <div class="cardTitle" *ngIf="!nearMiss.unresolved" [title]="nearMiss.label">{{ nearMiss.label }}</div>
                <div class="cardTitle unresolvedTitle" *ngIf="nearMiss.unresolved" i18n="@@MappingItemLoading">Loading this item&hellip;</div>

                <app-mapping-facet-grid [rows]="nearMiss.facetRows"
                                        [sequenceAnomaly]="nearMiss.sequenceAnomaly"></app-mapping-facet-grid>

                <!-- WHAT connected it. -->
                <div class="nearMissBlock">
                  <span class="nearMissLabel" i18n="@@MappingConnectedBy">Connected by</span>
                  <app-project-mapping-reason [reason]="nearMiss.reason"></app-project-mapping-reason>
                </div>

                <!--
                  WHY that was not enough.

                  Two sources, and the server's wins. `rejectedReason` is what
                  actually happened; `rejection` is a restatement of the rule
                  derived from the connection code, which is all the client can
                  honestly say on a server that has not shipped the field yet.
                  Keep BOTH branches - the fallback is the live path until the
                  server catches up.
                -->
                <div class="nearMissBlock nearMissWhyNot">
                  <span class="nearMissLabel" i18n="@@MappingNotIncludedBecause">Not included because</span>

                  <span class="nearMissWhyNotText" *ngIf="nearMiss.rejectedReason" [ngSwitch]="nearMiss.rejectedReason">
                    <span *ngSwitchCase="'CORROBORATION_ONLY'" i18n="@@NearMissCorroborationOnly">A shared cover on its own is never enough to group two items.</span>
                    <span *ngSwitchCase="'BELOW_THRESHOLD'" i18n="@@NearMissBelowThreshold">This connection was not strong enough to group them.</span>
                    <span *ngSwitchCase="'CONFLICTING_EVIDENCE'" i18n="@@NearMissConflictingEvidence">Some details matched, but others clearly did not.</span>
                    <!--
                      NO LONGER EMITTED (server, planner 1.6.0). The entire
                      cross-partition near-miss pass was deleted: a binding or
                      interior split is not a review question, it just happens.
                      Kept, like AMBIGUOUS_TARGET, so that if it ever returns
                      it reads as a sentence rather than a raw token.

                      Worth remembering WHY it went, because two of the three
                      reasons were things we rendered faithfully and could not
                      have caught from here: the wording never said the word
                      "binding", and the pass hardcoded one join reason per
                      scan, so cross-partition pairs were labelled
                      BODY_RESOURCE_ONLY - "titles differ" - over titles that
                      were byte-identical.
                    -->
                    <span *ngSwitchCase="'PARTITION_MISMATCH'" i18n="@@NearMissPartitionMismatch">This one is printed differently, so it is ordered as a separate product.</span>
                    <span *ngSwitchCase="'ALREADY_MAPPED'" i18n="@@NearMissAlreadyMapped">This one already belongs to another Project.</span>
                    <span *ngSwitchCase="'CLAIMED_BY_STRONGER'" i18n="@@NearMissClaimedByStronger">It matched another group more closely.</span>
                    <!--
                      The connection WAS strong enough; the group was simply at
                      its size limit. Reporting that as "not strong enough"
                      would send the reader hunting for missing evidence that
                      was never the problem. Fires ~345 times on the largest
                      organization in production, so it is not a corner case.
                    -->
                    <span *ngSwitchCase="'CLUSTER_FULL'" i18n="@@NearMissClusterFull">That group had already reached its maximum size.</span>
                    <!--
                      Never emitted (server 2026-10-26b §3). Kept only so that
                      if it ever appears it reads as a sentence rather than as
                      a raw code - nothing here depends on it.
                    -->
                    <span *ngSwitchCase="'AMBIGUOUS_TARGET'" i18n="@@NearMissAmbiguousTarget">It matched several groups equally well, so we left it out rather than guess.</span>
                    <span *ngSwitchDefault>{{ nearMiss.rejectedReason }}</span>
                  </span>

                  <span class="nearMissWhyNotText" *ngIf="!nearMiss.rejectedReason" [ngSwitch]="nearMiss.rejection">
                    <span *ngSwitchCase="'CORROBORATION_ONLY'" i18n="@@NearMissCorroborationOnly">A shared cover on its own is never enough to group two items.</span>
                    <span *ngSwitchCase="'TITLE_ONLY_WEAK'" i18n="@@NearMissTitleOnlyWeak">A similar title on its own was not enough to be sure.</span>
                    <span *ngSwitchDefault i18n="@@NearMissBelowThreshold">This connection was not strong enough to group them.</span>
                  </span>

                  <!--
                    "It went there" answers the question; "it was rejected"
                    merely raises it. Only sent for the two codes where another
                    group actually took the item.
                  -->
                  <span class="nearMissWentTo" *ngIf="nearMiss.rejectedToName">
                    <span i18n="@@NearMissWentTo">It is in</span>
                    <span class="nearMissWentToName">{{ nearMiss.rejectedToName }}</span>
                  </span>
                </div>
              </div>
            </div>
            <!--
              The server caps this list, strongest first: a common title can
              connect to hundreds of items and an uncapped set would dwarf the
              members it was rejected in favour of. Saying how many were cut
              keeps "considered but not included" honest - without it the list
              reads as exhaustive when it is not.
            -->
            <div class="nearMissMore" *ngIf="view.nearMissesTruncated > 0">
              <span i18n="@@MappingNearMissMore">More items were considered and not included</span>
              <span class="countValue">{{ view.nearMissesTruncated | numberFormat }}</span>
            </div>
          </div>

          <!--
            Exclusions and primary changes are LOCAL until a decision is sent.
            Pressing the minus button and walking away loses the change, and
            nothing said so - the flags existed on the view model and were
            rendered nowhere. This is the one state on the card where the
            reader can lose work by doing nothing.
          -->
          <div class="unsavedNote" *ngIf="view.edited && !view.saved && !view.saving && !view.applied">
            <mat-icon>edit</mat-icon>
            <span i18n="@@MappingUnsavedChanges">Your changes are not saved yet. Choose "Yes, these belong together" to keep them.</span>
          </div>

          <div class="proposalActions btn-sec" *ngIf="!view.applied">
            <!--
              Saying no is safe, and the wording says so: rejected members
              become Projects of their own on apply rather than being left
              unfiled. A customer who is unsure must not fear the No button.
            -->
            <button mat-stroked-button class="Discard-btn" [disabled]="view.saving || writeUnavailable" (click)="reject(view)">
              <span i18n="@@MappingRejectGroup">No, keep these separate</span>
            </button>
            <button mat-flat-button color="primary" class="Update-btn" [disabled]="view.saving || writeUnavailable" (click)="accept(view)">
              <span i18n="@@MappingAcceptGroup">Yes, these belong together</span>
            </button>
            <!--
              Create THIS Project now, without waiting for a bulk run.
              Uses the IDS selector, which the contract has carried since §3.5
              was written. Operator only, matching the other create actions -
              a customer's path is answer-then-apply.
            -->
            <button mat-stroked-button class="createOneButton" *ngIf="canCreateOne(view)"
                    [disabled]="applying || view.saving || writeUnavailable" (click)="createOne(view)">
              <mat-icon>playlist_add_check</mat-icon>
              <span i18n="@@MappingCreateThisProject">Create this Project now</span>
            </button>
            <!--
              Set aside for this session. NOT a decision about the grouping -
              "no" is what the Discard button records. This only removes the
              proposal from the next bulk create.
            -->
            <button mat-button class="holdButton" *ngIf="isOperator" (click)="toggleHold(view)">
              <span *ngIf="!isHeld(view)" i18n="@@MappingHoldBack">Hold back</span>
              <span *ngIf="isHeld(view)" i18n="@@MappingStopHolding">Stop holding back</span>
            </button>
          </div>
          <div class="rejectNote" *ngIf="!view.applied" i18n="@@MappingRejectNote">
            Saying no does not lose anything &mdash; each item simply gets a Project of its own.
          </div>
        </div>
      </mat-card>
    </div>

    <!--
      Repeated below the list as well as above it. After scrolling a batch of
      cards the control that reveals the next one is otherwise off-screen
      behind everything it just added.
    -->
    <div class="trimPanel trimPanelFoot" *ngIf="largeReview && hiddenProposalCount > 0">
      <mat-icon>filter_list</mat-icon>
      <div class="trimText">
        <span class="trimHint">
          <span i18n="@@MappingTrimmedHint">Others are already settled and are not shown, to keep this page quick</span>
          <span class="countValue">{{ hiddenProposalCount | numberFormat }}</span>
        </span>
      </div>
      <div class="trimActions">
        <button mat-stroked-button class="alternativesButton" *ngIf="hiddenAlternativeCount > 0"
                (click)="revealAlternatives()">
          <mat-icon>compare_arrows</mat-icon>
          <span i18n="@@MappingShowAlternatives">With alternatives</span>
          <span class="countValue">{{ hiddenAlternativeCount | numberFormat }}</span>
        </button>
        <button mat-stroked-button (click)="revealMore()">
          <span i18n="@@MappingShowMore">Show more</span>
          <span class="countValue">{{ revealBatchSize | numberFormat }}</span>
        </button>
        <button mat-button (click)="revealAll()">
          <span i18n="@@MappingShowAll">Show all</span>
          <span class="countValue">{{ proposalViews.length | numberFormat }}</span>
        </button>
      </div>
    </div>

    <!--
      A bulk create applies what the SERVER holds, so any unsaved local edit
      goes in with its original grouping. Said before the buttons, not after.
    -->
    <div class="unsavedNote unsavedBulkNote" *ngIf="unsavedCount > 0">
      <mat-icon>edit</mat-icon>
      <span i18n="@@MappingUnsavedBeforeApply">Some groups have changes that are not saved. Creating Projects now would use the original grouping for those.</span>
      <span class="countValue">{{ unsavedCount | numberFormat }}</span>
    </div>

    <div class="applyBar floatContainer" *ngIf="hasRun && proposalViews.length > 0">
      <div class="applySummary">
        <span i18n="@@MappingOutstandingQuestions">Questions still unanswered</span>
        <span class="countValue">{{ outstandingCount | numberFormat }}</span>
      </div>
      <div class="applyActions">
        <!--
          Operator default is NO_DECISION_NEEDED: build the uncontroversial
          Projects now and leave every open question for the customer.
        -->
        <button mat-stroked-button *ngIf="isOperator" [disabled]="applying || writeUnavailable"
                (click)="applySelector('NO_DECISION_NEEDED')">
          <span i18n="@@MappingApplyUncontroversial">Create the clear ones</span>
        </button>
        <!--
          Sends a plain ALL when nothing is held, so the ordinary path is
          unchanged; an explicit id list goes only when the operator has
          actually set something aside. The count is on the button because
          "all except some" must never be mistaken for "all".
        -->
        <button mat-flat-button color="primary" *ngIf="isOperator" [disabled]="applying || writeUnavailable" (click)="createAllExceptHeld()">
          <span *ngIf="heldCount === 0" i18n="@@MappingApplyAll">Create all proposed Projects</span>
          <span *ngIf="heldCount > 0" i18n="@@MappingApplyAllExceptHeld">Create all except those held back</span>
          <span class="countValue" *ngIf="heldCount > 0">{{ heldCount | numberFormat }}</span>
        </button>
        <button mat-flat-button color="primary" *ngIf="!isOperator" [disabled]="applying || writeUnavailable" (click)="applySelector(defaultSelector)">
          <span i18n="@@MappingApplyDecided">Apply my answers</span>
        </button>
      </div>
    </div>

  </ng-container>
</mat-card>

      </div>
    </mat-sidenav-content>
  </mat-sidenav-container>
</div>
